import { useState } from 'react';

import { exportCompetitionRegistrationsCsv } from '@/lib/api/competitionsApi';
import { PAY_STATUS_CONFIG, REG_STATUS_CONFIG } from '@/lib/status-config';
import { PaymentStatus, TeamRegistrationStatus } from '@shared/enums/domain.enum';
import { Download, X } from 'lucide-react';
import { toast } from 'sonner';

import {
  REGISTRATION_EXPORT_STATIC_COLUMNS,
  buildRegistrationExportColumns,
  isRegistrationExportConfigAvailable,
} from './registration-export-columns';

interface ExportRegistrationsModalProps {
  competitionId: number | null;
  competitionAlias: string | null;
  playersPerTeam: number | null | undefined;
  initialRegStatus?: TeamRegistrationStatus | '';
  initialPayStatus?: PaymentStatus | '';
  onClose: () => void;
}

// Demande client #5 — modale de filtres pour l'export CSV des inscriptions.
// Montée conditionnellement par le parent : l'état initial dérive des filtres
// courants de la page à chaque ouverture.
export function ExportRegistrationsModal({
  competitionId,
  competitionAlias,
  playersPerTeam,
  initialRegStatus = '',
  initialPayStatus = '',
  onClose,
}: ExportRegistrationsModalProps) {
  const [regStatuses, setRegStatuses] = useState<TeamRegistrationStatus[]>(initialRegStatus ? [initialRegStatus] : []);
  const [payStatuses, setPayStatuses] = useState<PaymentStatus[]>(initialPayStatus ? [initialPayStatus] : []);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [downloading, setDownloading] = useState(false);
  // null désigne la sélection complète par défaut, à chaque montage de la modale.
  const [customSelectedFields, setCustomSelectedFields] = useState<string[] | null>(null);
  const configurationReady = isRegistrationExportConfigAvailable(playersPerTeam);
  const columns = configurationReady ? buildRegistrationExportColumns(playersPerTeam ?? null) : [];
  const selectedFields = columns
    .filter(col => customSelectedFields === null || customSelectedFields.includes(col.key))
    .map(col => col.key);
  const allFieldsSelected = selectedFields.length === columns.length;
  const noFieldsSelected = configurationReady && selectedFields.length === 0;

  function toggleColumn(key: string) {
    setCustomSelectedFields(previous => {
      const current = previous ?? columns.map(col => col.key);
      return current.includes(key) ? current.filter(field => field !== key) : [...current, key];
    });
  }

  function resetColumns() {
    setCustomSelectedFields(null);
  }

  function toggleReg(status: TeamRegistrationStatus) {
    setRegStatuses(prev => (prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]));
  }

  function togglePay(status: PaymentStatus) {
    setPayStatuses(prev => (prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]));
  }

  async function handleExport() {
    if (!competitionId || !competitionAlias || !configurationReady) return;
    if (noFieldsSelected) {
      toast.error('Sélectionnez au moins une colonne à exporter');
      return;
    }
    if (dateFrom && dateTo && dateFrom > dateTo) {
      toast.error('La date de début doit précéder la date de fin');
      return;
    }
    setDownloading(true);
    try {
      await exportCompetitionRegistrationsCsv(competitionId, competitionAlias, {
        registrationStatus: regStatuses,
        paymentStatus: payStatuses,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        fields: allFieldsSelected ? undefined : selectedFields,
      });
      toast.success('Export CSV généré');
      onClose();
    } catch {
      toast.error("Erreur lors de l'export CSV");
    } finally {
      setDownloading(false);
    }
  }

  const activeFilters = regStatuses.length + payStatuses.length + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4'
      onClick={onClose}
      role='presentation'
    >
      <div
        className='w-full max-w-lg rounded-2xl bg-white shadow-xl'
        onClick={e => e.stopPropagation()}
        role='dialog'
        aria-modal='true'
        aria-label='Exporter les inscriptions'
      >
        <div className='flex items-center justify-between border-b border-slate-100 px-6 py-4'>
          <h2 className='flex items-center gap-2 text-lg font-bold text-slate-900'>
            <Download className='h-5 w-5 text-blue-600' />
            Exporter les inscriptions
          </h2>
          <button
            type='button'
            onClick={onClose}
            aria-label='Fermer'
            className='rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        <div className='max-h-[60vh] space-y-6 overflow-y-auto px-6 py-5'>
          <p className='text-sm text-slate-500'>
            Choisissez les équipes et les colonnes à inclure. Sans filtre, toutes les équipes sont exportées.
          </p>

          <div>
            <p className='mb-2 text-sm font-semibold text-slate-700'>Statut d&apos;inscription</p>
            <div className='flex flex-wrap gap-2'>
              {Object.entries(REG_STATUS_CONFIG).map(([value, cfg]) => {
                const status = value as TeamRegistrationStatus;
                const active = regStatuses.includes(status);
                return (
                  <button
                    key={value}
                    type='button'
                    onClick={() => toggleReg(status)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      active
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className='mb-2 text-sm font-semibold text-slate-700'>Statut de paiement</p>
            <div className='flex flex-wrap gap-2'>
              {Object.entries(PAY_STATUS_CONFIG).map(([value, cfg]) => {
                const status = value as PaymentStatus;
                const active = payStatuses.includes(status);
                return (
                  <button
                    key={value}
                    type='button'
                    onClick={() => togglePay(status)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      active
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className='mb-2 text-sm font-semibold text-slate-700'>
              Période d&apos;inscription <span className='font-normal text-slate-400'>(optionnel)</span>
            </p>
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
              <div>
                <label htmlFor='export-date-from' className='mb-1 block text-xs font-semibold text-slate-500'>
                  Du
                </label>
                <input
                  id='export-date-from'
                  type='date'
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  className='w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none'
                />
              </div>
              <div>
                <label htmlFor='export-date-to' className='mb-1 block text-xs font-semibold text-slate-500'>
                  Au
                </label>
                <input
                  id='export-date-to'
                  type='date'
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  className='w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none'
                />
              </div>
            </div>
          </div>
          <fieldset className='space-y-3 border-t border-slate-100 pt-5'>
            <legend className='text-sm font-semibold text-slate-700'>Colonnes à inclure</legend>
            {!configurationReady ? (
              <p className='rounded-lg bg-amber-50 p-3 text-sm text-amber-800' role='alert'>
                Impossible de connaître le nombre de joueurs de ce concours. Actualisez la page avant d&apos;exporter.
              </p>
            ) : (
              <>
                <div className='flex flex-wrap items-center justify-between gap-2'>
                  <span className='text-xs text-slate-500'>
                    {selectedFields.length} / {columns.length} colonnes sélectionnées
                  </span>
                  <div className='flex gap-3'>
                    <button
                      type='button'
                      onClick={resetColumns}
                      disabled={allFieldsSelected}
                      className='text-xs font-semibold text-blue-700 hover:underline disabled:cursor-default disabled:opacity-50'
                    >
                      Tout sélectionner
                    </button>
                    <button
                      type='button'
                      onClick={resetColumns}
                      disabled={allFieldsSelected}
                      className='text-xs font-semibold text-slate-600 hover:underline disabled:cursor-default disabled:opacity-50'
                    >
                      Réinitialiser
                    </button>
                  </div>
                </div>
                <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                  {columns.slice(0, REGISTRATION_EXPORT_STATIC_COLUMNS.length).map(col => (
                    <label key={col.key} className='flex cursor-pointer items-start gap-2 text-xs text-slate-700'>
                      <input
                        type='checkbox'
                        checked={selectedFields.includes(col.key)}
                        onChange={() => toggleColumn(col.key)}
                        className='mt-0.5 h-4 w-4 accent-blue-600'
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
                {Array.from({ length: playersPerTeam ?? 0 }, (_, index) => index + 1).map(n => (
                  <div key={n} className='border-t border-slate-100 pt-3'>
                    <p className='mb-2 text-xs font-semibold text-slate-600'>Joueur {n}</p>
                    <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                      {columns
                        .filter(col => col.key.startsWith(`players.${n}.`))
                        .map(col => (
                          <label key={col.key} className='flex cursor-pointer items-start gap-2 text-xs text-slate-700'>
                            <input
                              type='checkbox'
                              checked={selectedFields.includes(col.key)}
                              onChange={() => toggleColumn(col.key)}
                              className='mt-0.5 h-4 w-4 accent-blue-600'
                            />
                            <span>{col.label}</span>
                          </label>
                        ))}
                    </div>
                  </div>
                ))}
                {noFieldsSelected && (
                  <p className='text-xs font-semibold text-red-600' role='alert'>
                    Sélectionnez au moins une colonne pour exporter le CSV.
                  </p>
                )}
              </>
            )}
          </fieldset>
        </div>

        <div className='flex items-center justify-between gap-3 border-t border-slate-100 px-6 py-4'>
          <span className='text-xs text-slate-400'>
            {!configurationReady
              ? 'Configuration indisponible'
              : activeFilters === 0 && allFieldsSelected
                ? 'Aucun filtre — export complet'
                : `${activeFilters} filtre${activeFilters > 1 ? 's' : ''} de lignes · ${selectedFields.length}/${columns.length} colonnes`}
          </span>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={onClose}
              className='rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 transition hover:text-slate-900'
            >
              Annuler
            </button>
            <button
              type='button'
              onClick={() => void handleExport()}
              disabled={downloading || !competitionId || !competitionAlias || !configurationReady || noFieldsSelected}
              className='flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700 disabled:opacity-50'
            >
              <Download className='h-4 w-4' />
              {downloading ? 'Export…' : 'Exporter CSV'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
